import { useState, useEffect } from 'react';
import SharedButton from '../../global/Button/Button';
import { ChevronDownIcon, ChevronUpIcon, TableCellsIcon, TrashIcon } from '@heroicons/react/16/solid';
import { getFilters } from '../../services/savedFiltersService';
import { userAppContext } from '../../context/Context';
import { UserContext } from '../../types';

interface SavedColumn {
  id?: string;
  key?: string;
  name: string;
  columnVisibilityModel: any;
  columnOrder?: string[];
  savedAt: string;
  searchContext?: {
    view: string;
    tab: string;
    isEvidence: boolean;
  };
}

interface SavedColumnsBarProps {
  view: string;
  tab: string;
  isEvidence: boolean;
  onApplyColumns: (columns: SavedColumn) => void;
  onDeleteColumn: (id: string) => void;
}

const SavedColumnsBar = ({ view, tab, isEvidence, onApplyColumns, onDeleteColumn }: SavedColumnsBarProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [savedColumns, setSavedColumns] = useState<SavedColumn[]>([]);
  const { isAuthenticated } = userAppContext() as unknown as UserContext;

  useEffect(() => {
    loadSavedColumns();
  }, [view, tab, isEvidence]);

  const loadSavedColumns = async () => {
    const columns: SavedColumn[] = [];

    // Load from localStorage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('columnSelection_')) {
        try {
          const selection = JSON.parse(localStorage.getItem(key) || '');
          columns.push({
            key,
            ...selection,
          });
        } catch (error) {
          console.error('Error parsing saved column selection:', error);
        }
      }
    });

    // Load from DynamoDB if authenticated
    if (isAuthenticated) {
      try {
        const response = await getFilters();
        if (response.filters && Array.isArray(response.filters)) {
          const columnFilters = response.filters
            .filter(f => f.type === 'columns')
            .map(f => ({
              id: f.id,
              name: f.name,
              columnVisibilityModel: f.columnVisibilityModel,
              columnOrder: f.columnOrder,
              savedAt: f.createdAt || f.savedAt,
              searchContext: f.searchContext,
            }));
          columns.push(...columnFilters);
        }
      } catch (error) {
        console.error('Error loading saved columns from DynamoDB:', error);
      }
    }

    // Filter by current context (view, tab, isEvidence)
    const filteredColumns = columns.filter(col => {
      // Only show columns that have context and match current context
      if (!col.searchContext) return false;
      return (
        col.searchContext.view === view &&
        col.searchContext.tab === tab &&
        col.searchContext.isEvidence === isEvidence
      );
    });

    // Sort by date (most recent first)
    filteredColumns.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());

    setSavedColumns(filteredColumns);
  };

  if (savedColumns.length === 0) return null;

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TableCellsIcon className="w-5 h-5 text-purple-600" />
          <span className="text-sm font-medium text-gray-700">
            Saved Column Configurations ({savedColumns.length})
          </span>
        </div>
        <SharedButton size="xs" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? (
            <>
              Hide <ChevronUpIcon className="w-4 h-4 ml-1" />
            </>
          ) : (
            <>
              Show <ChevronDownIcon className="w-4 h-4 ml-1" />
            </>
          )}
        </SharedButton>
      </div>

      {isExpanded && (
        <div className="mt-3 space-y-2">
          {savedColumns.map((column, index) => (
            <div
              key={column.id || column.key || index}
              className="bg-white border border-purple-200 rounded p-3 flex items-center justify-between hover:bg-purple-50 transition-colors"
            >
              <div className="flex-1">
                <div className="font-medium text-gray-900">{column.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Saved: {new Date(column.savedAt).toLocaleDateString()} at{' '}
                  {new Date(column.savedAt).toLocaleTimeString()}
                </div>
                {column.columnVisibilityModel && (
                  <div className="text-xs text-gray-600 mt-1">
                    {Object.values(column.columnVisibilityModel).filter(Boolean).length} columns visible
                  </div>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <SharedButton size="xs" onClick={() => onApplyColumns(column)} variant="primary">
                  Apply
                </SharedButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedColumnsBar;
